import React, { useEffect, useState } from "react";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import ButtonCheckbox from "../../../components/ButtonCheckbox";

import { convertIntToMoney } from "../../../helpers/conversions";

/**
 * Escolha do que volta, a partir da nota original (BDM-887).
 *
 * O usuário não redigita nada nem procura o número do item na DANFE: marca a
 * linha e, se for devolução parcial, ajusta a quantidade. O teto é o que
 * ainda não voltou — devolver mais do que foi vendido é rejeição na certa.
 */
export const ModalReturnItems = ({ show, close, draft, onConfirm }) => {
    const [selection, setSelection] = useState({});

    useEffect(() => {
        if (!show || !draft) return;

        // Abre com tudo o que ainda pode voltar marcado: devolução inteira é
        // o caso comum, e desmarcar é mais rápido do que marcar item a item.
        setSelection(Object.fromEntries(
            draft.items
                .filter(item => item.available > 0)
                .map(item => [item.number, String(item.available)])
        ));
    }, [show, draft]);

    if (!draft) return null;

    const toggle = item => {
        setSelection(current => {
            const next = { ...current };

            if (next[item.number] !== undefined) {
                delete next[item.number];
            } else {
                next[item.number] = String(item.available);
            }

            return next;
        });
    };

    const changeQuantity = (item, value) => {
        const digits = (value ?? '').replace(/\D/g, '');
        // Trava no saldo: o campo simplesmente não aceita mais que isso.
        const limited = digits === '' ? '' : String(Math.min(parseInt(digits, 10), item.available));

        setSelection(current => ({ ...current, [item.number]: limited }));
    };

    const chosen = draft.items.filter(item => {
        const quantity = parseInt(selection[item.number], 10);
        return selection[item.number] !== undefined && quantity > 0;
    });

    const confirm = () => {
        onConfirm(chosen.map(item => ({
            ...item,
            quantity: parseInt(selection[item.number], 10),
        })));
        close();
    };

    const nothingLeft = draft.items.every(item => item.available <= 0);

    return (
        <Modal
            title={`O que está sendo devolvido`}
            size={`big`}
            show={show}
            close={close}
            footer={
                <>
                    <Button type={`secondary`} size={`small`} text={`Cancelar`} action={close} />
                    <Button
                        type={`primary`}
                        size={`small`}
                        text={`Usar ${chosen.length} ${chosen.length === 1 ? 'item' : 'itens'}`}
                        action={confirm}
                        disabled={chosen.length === 0}
                    />
                </>
            }
        >
            <div className="section">
                <p className="fiscal-hint">
                    Itens da nota {draft.document.number ? `nº ${draft.document.number}` : 'de origem'}
                    {draft.document.receiver_name ? ` — ${draft.document.receiver_name}` : ''}.
                    Marque o que volta e ajuste a quantidade na devolução parcial.
                </p>

                {nothingLeft &&
                    <p className="fiscal-hint warning">
                        Todos os itens desta nota já foram devolvidos.
                    </p>
                }

                <div className={`fiscal-return-items`}>
                    <div className={`return-head`}>
                        <span></span>
                        <span>Item</span>
                        <span>Vendido</span>
                        <span>Já devolvido</span>
                        <span>Devolver</span>
                    </div>

                    {draft.items.map(item => {
                        const disabled = item.available <= 0;

                        return (
                            <div className={`return-row ${disabled ? 'disabled' : ''}`} key={item.number}>
                                <ButtonCheckbox
                                    checked={selection[item.number] !== undefined}
                                    onClick={() => !disabled && toggle(item)}
                                />
                                <div className={`return-item`}>
                                    <strong>{item.number}. {item.description}</strong>
                                    <small>
                                        {item.ncm ? `NCM ${item.ncm}` : 'sem NCM'}
                                        {' · '}
                                        {convertIntToMoney(item.unit_price, true)} / {item.unit ?? 'UN'}
                                    </small>
                                </div>
                                <span>{item.quantity}</span>
                                <span className={item.returned > 0 ? 'returned' : ''}>
                                    {item.returned > 0 ? item.returned : '—'}
                                </span>
                                <div className={`return-quantity`}>
                                    {disabled
                                        ? <small>nada a devolver</small>
                                        : <Input
                                            floating={false}
                                            value={selection[item.number] ?? ''}
                                            change={value => changeQuantity(item, value)}
                                            disabled={selection[item.number] === undefined}
                                        />
                                    }
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Modal>
    );
};

export default ModalReturnItems;
